import React from 'react';
import moment from 'moment';
import { Button, Tag } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import Label from '../../../../components/EllipsisText';
import { formatClassname } from 'datastake-daf/src/helpers/ClassesHelper';

/**
 * 
 * @param {data, sections} param0 
 * @returns {Array} filtered data based on the sections
 */
export const filterData = ({ data = {}, sections = {}, options = {} }) => {
  const all = [];

  Object.keys(sections).forEach((k, i) => {
    const label = (sections[k] || {}).label;
    const children = data[k].map((ch, j) => ({
      ...ch,
      index: `${i + 1}.${j + 1}`,
      targetRisk: label,
      proponents: [ch.proponent],
      last: j === data[k].length - 1,
    }));

    const proponents = children
      .map((ch) => ch.proponent)
      .filter((item, pos) => item.indexOf(item) === pos);

    const statuses = children.map((ch) => ch.completionStatus);

    const status = statuses.includes('inProgress')
      ? 'inProgress' : statuses.includes('proposed')
        ? 'proposed' : statuses.includes('completed')
          ? 'completed' : null;

    if (children.length === 1) {
      all.push({
        ...children[0],
        index: i + 1,
        targetRisk: label,
        proponents,
        completionStatus: status,
        key: `section-${k}`,
      });
    } else {
      all.push({
        index: i + 1,
        targetRisk: label,
        typeOfMeasure: children.length,
        proponents,
        completionStatus: status,
        key: `section-${k}`,
        children,
      });
    }
  });

  return all;
};

/**
 * 
 * @param {status, t} param0 
 * @returns {} rendered status
 */
export const renderStatus = ({
  status,
  t = (s) => s,
}) => {
  switch (status) {
    case 'inProgress':
      return (
        <Tag className="tag-in-progress">
          {t('In Progress')}
        </Tag>
      );
    case 'proposed':
      return (
        <Tag color="purple">{t('Proposed')}</Tag>
      );
    case 'completed':
      return (
        <Tag color="green">{t('Completed')}</Tag>
      );
    default:
      return (
        <Tag color="purple">{t('Proposed')}</Tag>
      );
  }
};

/**
 * 
 * @param {label, t} param0 
 * @returns {} rendered objective
 */
export const renderObjective = ({
  label,
  t = (s) => s,
}) => <Tag color="blue">{t(label)}</Tag>;

/**
 *
 * @param {t, maxWidth, modalActions, expanded, toggle, unToggle} param0
 * @returns {Array} Rendered table columns
 */
export const getColumns = ({
  t = (s) => s,
  maxWidth = '100%',
  modalActions = () => { },
  expanded = [],
  toggle = () => { },
  unToggle = () => { },
}) => [
    {
      title: 'No.',
      key: 'index',
      dataIndex: 'index',
      sorter: (a, b) => a.index - b.index,
      width: 105,
      render: (a, all) => {
        return a.toString().indexOf('.') === -1 ? (
          <div style={{ textAlign: 'right', paddingRight: '10%', wordBreak: 'keep-all' }}>
            {a}
          </div>
        ) : (
          <div className="expanded-row-container" style={{ textAlign: 'right', paddingRight: '10%', wordBreak: 'keep-all' }}>
            <div className={formatClassname(['line', all.last && 'last'])} />
            <div className="left-line" />
            {a}
          </div>
        );
      },
    },
    {
      title: t('Target Risk'),
      key: 'targetRisk',
      dataIndex: 'targetRisk',
      sorter: (a, b) => ('' + a.targetRisk).localeCompare(b.targetRisk),
      render: (text) => <Label text={text} maxWidth={maxWidth} />,
    },
    {
      title: t('Type of Measure'),
      key: 'typeOfMeasure',
      dataIndex: 'typeOfMeasure',
      ellipsis: true,
      sorter: (a, b) => a.typeOfMeasure - b.typeOfMeasure,
      render: (a, all) => {
        if (a !== undefined) {
          return (
            <Label
              text={all.children ? `${a} ${t('Measures')}` : a}
              maxWidth={maxWidth}
              style={{
                color: all.children ? 'var(--mmt-primary-70)' : null,
                cursor: all.children ? 'pointer' : null,
              }}
              onClick={() => {
                if (all.children) {
                  if (expanded.includes(all.key)) {
                    unToggle(all.key);
                  } else {
                    toggle(all.key);
                  }
                }
              }}
            />
          )
        }
        return all.measure;
      },
    },
    {
      title: t('Proponent'),
      key: 'proponents',
      dataIndex: 'proponents',
      sorter: (a, b) => (a.proponents || []).length - (b.proponents || []).length,
      render: (a) => (a || []).length > 1 ? (
        <div style={{ color: 'var(--mmt-primary-70)' }} className="clickable">
          <Label
            text={`${(a || []).length} ${t('proponents')}`}
            maxWidth={maxWidth}
          />
        </div>
      ) : (a || []).length === 1 ? (
        <Label
          text={a[0]}
          maxWidth={maxWidth}
        />
      ) : <Label
        text={t('No proponents')}
        maxWidth={maxWidth}
      />
      ,
    },
    {
      title: t('Status'),
      key: 'completionStatus',
      dataIndex: 'completionStatus',
      sorter: (a, b) => ('' + a.completionStatus).localeCompare(b.completionStatus),
      render: (a) => (
        <div className="status-cont">
          {renderStatus({ status: a, t })}
        </div>
      ),
    },
    {
      title: t('Submission Date'),
      key: 'proposedDate',
      dataIndex: 'proposedDate',
      sorter: (a, b) => moment(a.proposedDate).diff(moment(b.proposedDate)),
      width: 150,
      ellipsis: true,
      render: (date, all) => date ? (
        <div className="submission-date">
          <Label
            maxWidth={maxWidth}
            text={moment(date).format('DD MMM YYYY')}
          />
          <Button
            onClick={() => {
              if (typeof modalActions === 'function') {
                modalActions(all);
              }
            }}
            icon={<EyeOutlined />}
            size="small"
          />
        </div>
      ) : null,
    },
  ];
